'use client';

import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle, PropsWithChildren } from 'react';
import {Button, message, notification} from 'antd';
import { AudioOutlined, AudioMutedOutlined, LoadingOutlined,CloseOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import {getVoiceAiConfig, getVoiceAiTreatment} from "../patient/patientSlice";
import {unwrapResult} from "@reduxjs/toolkit";
import {useDispatch} from "react-redux";
import {RootDispatch} from "../../app/store";

// 暴露给外部的ref方法类型
export interface XfyunRTASRRef {
  stopRecord: () => void;
}


// HMAC-SHA1 计算（对齐官方Java版）
async function hmacSha1(message: string, secret: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-1' },
    false,
    ['sign']
  );
  return crypto.subtle.sign('HMAC', key, encoder.encode(message));
}

// Base64 编码（修复TS2569迭代错误，无需downlevelIteration）
function base64Encode(buffer: ArrayBuffer): string {
  const uint8Array = new Uint8Array(buffer);
  // 替换扩展运算符：用循环拼接字符，避免迭代定型数组
  let charStr = '';
  for (let i = 0; i < uint8Array.length; i++) {
    charStr += String.fromCharCode(uint8Array[i]);
  }
  return btoa(charStr);
}

// 生成UTC时间（严格对齐官方Java版：yyyy-MM-dd'T'HH:mm:ssZ → +0800）
function getUtcTime(): string {
  const date = new Date();
  const tzOffset = date.getTimezoneOffset() * 60000;
  const localTime = date.getTime() - tzOffset;
  const utcDate = new Date(localTime); // GMT+8
  const year = utcDate.getUTCFullYear();
  const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(utcDate.getUTCDate()).padStart(2, '0');
  const hours = String(utcDate.getUTCHours()).padStart(2, '0');
  const minutes = String(utcDate.getUTCMinutes()).padStart(2, '0');
  const seconds = String(utcDate.getUTCSeconds()).padStart(2, '0');
  // 输出格式：2026-02-26T11:22:33+0800（完全对齐Java的SimpleDateFormat）
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+0800`;
}

// 生成UUID（无依赖，对齐Java的UUID.randomUUID()）
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}


// 识别结果接口（对齐官方Java版返回格式）
interface RecognitionResult {
  msg_type?: string;
  data?: {
    sessionId?: string;
    cn?: {
      st?: {
        rt?: Array<{
          ws?: Array<{
            cw?: Array<{
              w: string,
              rl: string,
            }>;
          }>;
        }>;
        type?: string;
      };
    };
    desc?: string;
  };
  action?: string;
  code?: string;
  desc?: string;
}

// 组件props接口
export interface XfyunRTASRProps {
  onResult?: (text: string, isFinal: boolean) => void;
  onError?: (error: string) => void;
  onFinish?: () => void;
  closeModal?:() =>void;
}

// 核心组件（对齐官方Java版交互逻辑）
const XfyunRTASRSimpleLLM = forwardRef<XfyunRTASRRef, XfyunRTASRProps>(
  ({  onResult, onError,onFinish,closeModal }: PropsWithChildren<XfyunRTASRProps>, ref:any) => {
    const dispatch = useDispatch<RootDispatch>()
    const [messageApi, contextHolder] = message.useMessage()

    // 基础状态
    const [isRecording, setIsRecording] = useState(false);
    const [status, setStatus] = useState<'idle' | 'connecting' | 'recording' | 'error'>('idle');
    const [currentText, setCurrentText] = useState('点击开始说话');
    const [isLoadingTransition, setIsLoadingTransition] = useState(false);
    const isRecordingRef = useRef<boolean>(false);

    // 新增：文本容器ref，用于滚动控制
    const textContainerRef = useRef<HTMLDivElement>(null);

    // 核心Ref（对齐官方Java版：sessionId从服务端获取）
    const xfyunWsRef = useRef<WebSocket | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const processorRef = useRef<AudioWorkletNode | null>(null);
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const sessionIdRef = useRef<string>(''); // 从服务端action消息提取
    const audioCacheRef = useRef<Int16Array>(new Int16Array());
    // Canvas相关Ref
    const waveformCanvasRef = useRef<HTMLCanvasElement>(null);
    const silentPhaseRef = useRef<number>(0);
    const transitionProgressRef = useRef<number>(0);
    const loadingPhaseRef = useRef<number>(0);
    const micErrorRef = useRef<boolean>(false); // 标记麦克风是否初始化失败

    // 暴露停止录音方法
    useImperativeHandle(ref, () => ({
      stopRecord: stopRecording,
    }));

    // 同步state和ref
    useEffect(() => {
      isRecordingRef.current = isRecording;
    }, [isRecording]);

    useEffect(() => {
      if (textContainerRef.current) {
        const el = textContainerRef.current;
        el.scrollLeft = el.scrollWidth;
      }
    }, [currentText]);

    // 发送结束标识（对齐官方Java版：用服务端返回的sessionId）
    const sendEndFrame = useCallback(() => {
      if (xfyunWsRef.current && xfyunWsRef.current.readyState === WebSocket.OPEN) {
        const endMsg = {
          end: true,
          sessionId: sessionIdRef.current || '' // 优先用服务端的sessionId
        };
        const endMsgStr = JSON.stringify(endMsg);
        xfyunWsRef.current.send(endMsgStr);
      }
    }, []);

    let tempFinish = false;
    // 处理识别结果（对齐官方Java版消息格式）
    const handleRecognitionResult = useCallback((result: RecognitionResult) => {
      // 1. 提取服务端返回的sessionId（核心修复：不再本地生成）
      if (result.msg_type === 'action' && result.data?.sessionId) {
        sessionIdRef.current = result.data.sessionId;
        return;
      }

      // 2. 处理ASR识别结果
      if (result.msg_type === 'result' && result.data?.cn?.st?.rt) {
        try {
          const { data } = result;
          let text = '';
          const isFinal = data?.cn?.st?.type == '0';

          // 解析文字（对齐官方Java版解析逻辑）
          data?.cn?.st?.rt?.forEach((rtItem) => {
            rtItem.ws?.forEach((wsItem) => {
              wsItem.cw?.forEach((cwItem) => {
                text += cwItem.w;
              });
            });
          });

          if (text) {
            if(!isFinal){
              if(tempFinish){
                setCurrentText(text);
                tempFinish = false;
              }else{
                setCurrentText(prev => prev + text);
              }
            }else{
              setCurrentText(text);
              tempFinish = true;
            }
            onResult?.(text, isFinal);
          }
        } catch (e) {
          console.error('解析识别结果失败:', e);
        }
      }

      // 3. 处理错误
      if (result.action === 'error' || (result.msg_type === 'result' && result.data?.desc)) {
        const errorDesc = result.data?.desc || result.desc || '识别出错';
        setStatus('error');
        onError?.(errorDesc);
        message.error(`接口错误：${errorDesc}`);
      }
    }, [onResult, onError]);

    // 静默波纹绘制（保留原有样式）
    const drawSilentWave = useCallback(() => {
      const canvas = waveformCanvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const silentConfig = {
        waveCount: 3,
        waveSpeed: 0.008,
        baseAlpha: 0.12,
        alphaStep: 0.06,
        baseAmplitude: 3,
        amplitudeStep: 1.5,
        baseLineWidth: 1.2,
        lineWidthStep: 0.5,
        pointStep: 5,
        color: { right: '37, 99, 235', left: '144, 202, 249' }
      };

      const TWO_PI = Math.PI * 2;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgb(255,255,255)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const baseY = canvas.height / 2;
      const halfWidth = canvas.width / 2;

      for (let w = 0; w < silentConfig.waveCount; w++) {
        ctx.beginPath();
        const decay = Math.pow(0.9, w);
        const currentAlpha = silentConfig.baseAlpha + (silentConfig.alphaStep * w) * decay;
        const currentAmplitude = (silentConfig.baseAmplitude + (silentConfig.amplitudeStep * w)) * decay;
        const currentLineWidth = Math.max(
          silentConfig.baseLineWidth,
          (silentConfig.baseLineWidth + (silentConfig.lineWidthStep * w)) * decay
        );

        const gradient = ctx.createLinearGradient(canvas.width, 0, 0, 0);
        gradient.addColorStop(0, `rgba(${silentConfig.color.right}, ${currentAlpha})`);
        gradient.addColorStop(1, `rgba(${silentConfig.color.left}, ${currentAlpha})`);

        ctx.strokeStyle = gradient;
        ctx.lineWidth = currentLineWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        for (let i = 0; i <= canvas.width; i += silentConfig.pointStep) {
          const normalizedX = i / canvas.width;
          const xFromCenter = Math.abs(i - halfWidth) / halfWidth;
          const centerDecay = 1 - Math.pow(xFromCenter, 1.5);

          const wave = Math.sin(normalizedX * Math.PI * 2 + silentPhaseRef.current + w * Math.PI / 3) * 0.6;
          const y = baseY + wave * currentAmplitude * centerDecay;
          i === 0 ? ctx.moveTo(i, y) : ctx.lineTo(i, y);
        }
        ctx.stroke();
      }

      silentPhaseRef.current += silentConfig.waveSpeed;
      silentPhaseRef.current = silentPhaseRef.current % TWO_PI;
      if (!isLoadingTransition) {
        animationFrameRef.current = requestAnimationFrame(drawSilentWave);
      }
    }, [isLoadingTransition]);

    // 动态波纹绘制（保留原有样式）
    const drawWaveform = useCallback(() => {
      const canvas = waveformCanvasRef.current;
      const analyser = analyserRef.current;
      if (!canvas || !analyser || isLoadingTransition) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const config = {
        waveCount: 5,
        waveSpeed: 0.02,
        baseAlpha: 0.15,
        alphaStep: 0.08,
        baseAmplitude: 8,
        amplitudeStep: 4,
        baseLineWidth: 1,
        lineWidthStep: 0.8,
        volumeScale: 300,
        maxVolume: 100,
        pointStep: 4,
        color: { right: '37, 99, 235', left: '144, 202, 249' }
      };

      analyser.smoothingTimeConstant = 0.85;
      analyser.fftSize = 2048;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      let phase = 0;
      const wavePhases = Array(config.waveCount).fill(0).map((_, i) => i * Math.PI / 4);
      const TWO_PI = Math.PI * 2;

      const draw = () => {
        if (!canvas || !analyser || !isRecordingRef.current || isLoadingTransition) {
          drawSilentWave();
          return;
        }
        animationFrameRef.current = requestAnimationFrame(draw);

        try {
          analyser.getByteTimeDomainData(dataArray);
        } catch (e) {
          drawSilentWave();
          return;
        }

        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          const val = (dataArray[i] - 128) / 128;
          sum += val * val;
        }
        const rms = Math.sqrt(sum / bufferLength);
        const volume = Math.min(rms * config.volumeScale, config.maxVolume);
        const volumeRatio = volume / config.maxVolume;

        ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const baseY = canvas.height / 2;
        const halfWidth = canvas.width / 2;

        for (let w = 0; w < config.waveCount; w++) {
          ctx.beginPath();
          const decay = Math.pow(0.88, w);
          const currentAlpha = config.baseAlpha + (config.alphaStep * w * volumeRatio) * decay;
          const currentAmplitude = (config.baseAmplitude + (config.amplitudeStep * w * volumeRatio * 2)) * decay;
          const currentLineWidth = Math.max(
            config.baseLineWidth,
            (config.baseLineWidth + (config.lineWidthStep * w * volumeRatio)) * decay
          );

          const gradient = ctx.createLinearGradient(canvas.width, 0, 0, 0);
          gradient.addColorStop(0, `rgba(${config.color.right}, ${currentAlpha})`);
          gradient.addColorStop(1, `rgba(${config.color.left}, ${currentAlpha})`);

          ctx.strokeStyle = gradient;
          ctx.lineWidth = currentLineWidth;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';

          for (let i = 0; i <= canvas.width; i += config.pointStep) {
            const normalizedX = i / canvas.width;
            const xFromCenter = Math.abs(i - halfWidth) / halfWidth;
            const centerDecay = 1 - Math.pow(xFromCenter, 1.2);

            const wave1 = Math.sin(normalizedX * Math.PI * 2 + phase + wavePhases[w]) * 0.5;
            const wave2 = Math.sin(normalizedX * Math.PI * 4.2 + phase * 1.4 + wavePhases[w]) * 0.3;
            const wave3 = Math.sin(normalizedX * Math.PI * 6.8 + phase * 1.8 + wavePhases[w]) * 0.15;
            const totalWave = wave1 + wave2 + wave3;

            const y = baseY + totalWave * currentAmplitude * centerDecay * (1 + volumeRatio);
            i === 0 ? ctx.moveTo(i, y) : ctx.lineTo(i, y);
          }
          ctx.stroke();
        }

        phase += config.waveSpeed * (1 + volumeRatio * 0.9);
        phase = phase % TWO_PI;

        wavePhases.forEach((_, i, arr) => {
          arr[i] += config.waveSpeed * 0.3 * (i + 1);
          arr[i] = arr[i] % TWO_PI;
        });
      };
      draw();
    }, [drawSilentWave, isLoadingTransition]);

// 生成鉴权URL（完全复刻官方Java版逻辑）
    async function generateXfyunAuthUrl(): Promise<string> {

      return dispatch(getVoiceAiConfig({}))
        .then(unwrapResult)
        .then((config: any) => {
          return config?.url || "";
        })
        .catch((error) => {
          setStatus('error');
          onError?.("服务器繁忙，请稍后再试1");
          return '';
        });
    }
    // Loading动画绘制（保留原有样式）
    const drawLoadingCircle = useCallback(() => {
      const canvas = waveformCanvasRef.current;
      if (!canvas || !isLoadingTransition) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const config = {
        circleRadius: Math.min(canvas.width, canvas.height) / 3,
        lineWidth: 1.8,
        color: { right: '37, 99, 235', left: '144, 202, 249' },
        speed: 0.06,
        alpha: 0.25
      };

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgb(255,255,255)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const progress = transitionProgressRef.current;

      const gradient = ctx.createLinearGradient(centerX + config.circleRadius, centerY, centerX - config.circleRadius, centerY);
      gradient.addColorStop(0, `rgba(${config.color.right}, ${config.alpha})`);
      gradient.addColorStop(1, `rgba(${config.color.left}, ${config.alpha})`);

      ctx.strokeStyle = gradient;
      ctx.lineWidth = config.lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      const startAngle = loadingPhaseRef.current;
      const endAngle = startAngle + Math.PI * 2.4 * progress + Math.PI * 0.2 * (1 - progress);
      const radius = config.circleRadius * progress + 40 * (1 - progress);

      for (let i = 0; i <= 360; i += 5) {
        const angle = (i * Math.PI) / 180;
        const x = centerX + Math.cos(angle) * radius * (1 - Math.pow(Math.sin(angle * 2), 2) * (1 - progress));
        const y = centerY + Math.sin(angle) * radius * (1 - Math.pow(Math.cos(angle * 2), 2) * (1 - progress));
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.stroke();

      if (progress < 1) transitionProgressRef.current += 0.016;
      loadingPhaseRef.current += config.speed;
      loadingPhaseRef.current = loadingPhaseRef.current % (Math.PI * 2);

      if (isLoadingTransition) {
        animationFrameRef.current = requestAnimationFrame(drawLoadingCircle);
      } else {
        transitionProgressRef.current = 0;
        drawSilentWave();
      }
    }, [drawSilentWave, isLoadingTransition]);

    // 停止所有波纹绘制
    const stopAllWave = useCallback(() => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }, []);

    // 处理音频数据（对齐官方Java版：40ms/1280字节）
    const processAudioData = async (audioContext: AudioContext, mediaStream: MediaStream) => {
      const workletCode = `
        class PCMProcessor extends AudioWorkletProcessor {
          process(inputs, outputs) {
            const input = inputs[0];
            if (input.length > 0 && input[0].length > 0) {
              const channelData = input[0];
              const pcmData = new Int16Array(channelData.length);
              for (let i = 0; i < channelData.length; i++) {
                const sample = Math.max(-1, Math.min(1, channelData[i]));
                pcmData[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
              }
              this.port.postMessage({ pcmData: pcmData.buffer }, [pcmData.buffer]);
            }
            return true;
          }
        }
        registerProcessor('pcm-processor', PCMProcessor);
      `;

      const blob = new Blob([workletCode], { type: 'application/javascript' });
      const workletUrl = URL.createObjectURL(blob);
      await audioContext.audioWorklet.addModule(workletUrl);

      // 初始化音频链路
      const source = audioContext.createMediaStreamSource(mediaStream);
      sourceRef.current = source;

      const analyser = audioContext.createAnalyser();
      analyserRef.current = analyser;

      const processor = new AudioWorkletNode(audioContext, 'pcm-processor');
      processorRef.current = processor;

      // 音频流走向
      source.connect(analyser);
      analyser.connect(processor);
      processor.connect(audioContext.destination);

      // 音频帧发送（对齐官方Java版：1280字节/40ms）
      const FRAME_COUNT = 640; // 1280字节 = 640 * 2
      processor.port.onmessage = (event) => {
        if (!xfyunWsRef.current || xfyunWsRef.current.readyState !== WebSocket.OPEN) return;

        const newPcm = new Int16Array(event.data.pcmData);
        // 拼接缓存（修复TS迭代错误）
        const combinedLength = audioCacheRef.current.length + newPcm.length;
        const combinedPcm = new Int16Array(combinedLength);
        combinedPcm.set(audioCacheRef.current, 0);
        combinedPcm.set(newPcm, audioCacheRef.current.length);
        audioCacheRef.current = combinedPcm;

        // 按帧发送
        while (audioCacheRef.current.length >= FRAME_COUNT) {
          const sendData = audioCacheRef.current.subarray(0, FRAME_COUNT);
          xfyunWsRef.current.send(sendData.buffer);
          audioCacheRef.current = audioCacheRef.current.subarray(FRAME_COUNT);
        }
      };
    };

    // 初始化麦克风
    const initAudioProcessing = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: 16000,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true
          }
        });
        mediaStreamRef.current = stream;

        const audioContext = new AudioContext({ sampleRate: 16000 });
        audioContextRef.current = audioContext;
        if (audioContext.state === 'suspended') await audioContext.resume();
        await processAudioData(audioContext, stream);
        micErrorRef.current = false; // 重置麦克风错误标记
        return true;
      } catch (error) {
        micErrorRef.current = true; // 标记麦克风初始化失败
        messageApi.open({
          type: 'warning',
          content: '无法访问麦克风，请确认您的电脑接入了麦克风设备并开启了权限。',
          className: 'custom-class',
          style: {
            zIndex:1000,
            marginTop: '5vh',
          },
        })
        setStatus('error');
        return false;
      }
    };

    // 清理音频资源
    const cleanupAudio = useCallback(() => {
      stopAllWave();
      audioCacheRef.current = new Int16Array();
      if (processorRef.current) processorRef.current.disconnect();
      if (sourceRef.current) sourceRef.current.disconnect();
      if (audioContextRef.current) audioContextRef.current.close();
      if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach(track => track.stop());

      processorRef.current = null;
      sourceRef.current = null;
      audioContextRef.current = null;
      mediaStreamRef.current = null;
      analyserRef.current = null;
    }, [stopAllWave]);

    // 开始录音（对齐官方Java版连接逻辑）
    const startRecording = async () => {
      setCurrentText("")

      setStatus('connecting');
      setCurrentText('');
      audioCacheRef.current = new Int16Array();
      sessionIdRef.current = ''; // 重置sessionId
      micErrorRef.current = false; // 重置麦克风错误标记

      try {
        // 1. 生成官方对齐的WS URL
        const fullWsUrl = await generateXfyunAuthUrl();

        // 2. 创建WebSocket客户端
        const ws = new WebSocket(fullWsUrl);
        xfyunWsRef.current = ws;

        // 3. 超时处理（15秒，对齐Java版）
        const timeoutId = setTimeout(() => {
          if (micErrorRef.current) return;
          message.error('连接超时，请检查配置和网络');
          setStatus('error');
          setIsRecording(false);
          cleanupAudio();
          ws.close();
        }, 15000);

        // 4. WebSocket事件（对齐官方Java版）
        ws.onopen = () => {
          clearTimeout(timeoutId);
          setStatus('recording');
          setIsRecording(true);
          stopAllWave();
          setTimeout(() => drawWaveform(), 200);
        };

        ws.onmessage = (event) => {
          try {
            const result: RecognitionResult = JSON.parse(event.data);
            handleRecognitionResult(result);
          } catch (e) {
            console.error('解析消息失败:', e, '原始消息:', event.data);
          }
        };

        ws.onerror = (error) => {
          clearTimeout(timeoutId);
          if (micErrorRef.current) {
            ws.close(); // 主动关闭WebSocket
            cleanupAudio();
            setIsRecording(false);
            setStatus('idle');
            return;
          }
          message.error('WebSocket连接错误，请检查配置/网络');
          setStatus('error');
          setIsRecording(false);
          cleanupAudio();
        };

        ws.onclose = (e) => {
          clearTimeout(timeoutId);
          if (isRecording) {
            stopRecording();
          }
        };

        // 5. 初始化音频
        const audioReady = await initAudioProcessing();
        if (!audioReady) {
          clearTimeout(timeoutId);
          ws.close();
        }
      } catch (error) {
        message.error(`启动失败：${(error as Error).message}`);
        setStatus('error');
        setIsRecording(false);
        cleanupAudio();
      }
    };

    // 停止录音（对齐官方Java版结束逻辑）
    const stopRecording = () => {
      setStatus('idle');
      stopAllWave();

      // Loading动画
      setIsLoadingTransition(true);
      transitionProgressRef.current = 0;
      drawLoadingCircle();

      // 发送结束标识（用服务端返回的sessionId）
      setTimeout(() => {
        sendEndFrame();
        setTimeout(() => {
          if (xfyunWsRef.current) {
            xfyunWsRef.current.close();
            xfyunWsRef.current = null;
          }
          cleanupAudio();
          setIsLoadingTransition(false);
          if(isRecording){
            setIsRecording(false);
            onFinish?.();
          }

        }, 10);
      }, 10);
    };

    // 组件生命周期
    useEffect(() => {
      // 初始化Canvas
      const canvas = waveformCanvasRef.current;
      if (canvas) {
        canvas.width = 200;
        canvas.height = 80;
      }
      drawSilentWave();

      // 卸载清理
      return () => {
        if (xfyunWsRef.current) xfyunWsRef.current.close();
        cleanupAudio();
        stopAllWave();
        setIsLoadingTransition(false);
        transitionProgressRef.current = 0;
      };
    }, [cleanupAudio, drawSilentWave, stopAllWave]);


    return (
      <>{contextHolder}
      <div
        style={{
          position: 'fixed',
          zIndex: 999,
          top: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
          padding: 16,
          backgroundColor: '#fff',
          borderRadius: 12,
          boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
          width: 240,
        }}
      >
        <button
          onClick={()=>{
            // Loading动画
            setIsLoadingTransition(true);
            transitionProgressRef.current = 0;
            drawLoadingCircle();
            // 发送结束标识（用服务端返回的sessionId）
            setTimeout(() => {
              sendEndFrame();
              setTimeout(() => {
                if (xfyunWsRef.current) {
                  xfyunWsRef.current.close();
                  xfyunWsRef.current = null;
                }
                cleanupAudio();
                setIsLoadingTransition(false);
                // if(isRecording){
                  setIsRecording(false);
                  closeModal && closeModal()
                // }

              }, 10);
            }, 10);
          }} // 点击关闭的回调函数
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 24,
            height: 24,
            borderRadius: 50,
            border: 'none',
            backgroundColor: 'rgba(0,0,0,0.05)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            // 鼠标悬停效果
            // 去掉点击时的默认边框
          }}
          aria-label="关闭" // 无障碍属性
        >
          <CloseOutlined
            style={{
              fontSize: 14,
              color: '#666'
            }}
          />
        </button>
        <canvas
          ref={waveformCanvasRef}
          style={{
            width: '100%',
            height: 80,
            borderRadius: 8,
            backgroundColor: 'rgb(255,255,255)',
          }}
        />

        {/* 核心修改：文本容器样式实现渐变滚动效果 */}
        <div
          ref={textContainerRef}
          style={{
            width: '100%',
            height: 36,
            lineHeight: '36px',
            textAlign: (currentText == "点击开始说话" || status === 'connecting' )? "center":'center',        // 这里改成 right
            fontSize: 14,
            color: currentText ? '#333' : '#999',
            padding: '0 8px',
            overflowX: 'auto',
            overflowY: 'hidden',
            whiteSpace: 'nowrap',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%)',
            maskImage: 'linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%)',
            direction: 'rtl',          // 关键：右到左
          }}
        >
          {currentText || (status === 'connecting' ? '连接中...' : '')}
        </div>

        <Button
          size="middle"
          loading={status === 'connecting'}
          icon={status === 'connecting' ? <LoadingOutlined spin /> : (isRecording ? <AudioMutedOutlined /> : <AudioOutlined />)}
          onClick={isRecording ? stopRecording : startRecording}
          style={{ borderRadius: 20, width: 100, height: 36 }}
        >
          {status === 'connecting' ? '连接中' : isRecording ? '停止' : '开始'}
        </Button>
      </div>
      </>
    );
  }
);

// 组件配置
XfyunRTASRSimpleLLM.displayName = 'XfyunRTASRSimpleLLM';
XfyunRTASRSimpleLLM.propTypes = {
  onResult: PropTypes.func,
  onError: PropTypes.func,
};
XfyunRTASRSimpleLLM.defaultProps = {
  onResult: () => {
    //
  },
  onError: () => {
    //
  },
};

export default XfyunRTASRSimpleLLM;